'use client';

import { useMemoizedFn, useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useState } from 'react';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { useDevice, useFetchRecreateData, useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  CreationWithTask,
  FormImagePrompt,
  ImageUploadFile,
  TaskInfo,
  ToolTaskFormVideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import { isExtendTask, isVideoDurationWithinRange, normalizeDiscreteVideoDurationToSeconds } from '@/app/utils';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { MAX_REFERENCE_IMAGE_COUNT, MultiImageUpload } from '../_components/MultiImageUpload';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { useRunTempUploadBatch } from '../_hooks/use-run-temp-upload-batch';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolTaskRecreatePatch,
  createPromptListHelpers,
  createToolImagePromptFromUploadFile,
  createToolVideoPromptFromUploadFile,
  createVideoPromptFromCreation,
} from '../_utils';
import { getToolTempImagesPendingItems, getToolTempVideosPendingItems } from '../_utils/tool-temp-upload-items';
import { VideoRemakeSettings } from './_components/VideoRemakeSettings';

const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024;
const minVideoDuration = 5;
const maxVideoDuration = 60;
const minImageCount = 1;

interface VideoRemakeState {
  selectedSourceCreation?: CreationWithTask;
}

const VideoRemakeForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();

  const { settings, updateSettings } = useToolForm('remake');
  const storeApi = useStoreApi();

  const [openSourceVideoSelector, setOpenSourceVideoSelector] = useState(false);
  const [selectedSourceCreationIndex, setSelectedSourceCreationIndex] = useState<number | null>(null);

  const [videoRemakeState, setVideoRemakeState] = useSetState<VideoRemakeState>({
    selectedSourceCreation: undefined,
  });

  const { updateRemakeFormParams, imagePrompts, videoPrompts, creationId, textPrompt, userInfo } = useStore(
    (state) => ({
      updateRemakeFormParams: state.updateRemakeFormParams,
      imagePrompts: state.remakeFormParams.imagePrompts,
      videoPrompts: state.remakeFormParams.videoPrompts,
      creationId: state.remakeFormParams.creationId,
      textPrompt: state.remakeFormParams.textPrompt || '',
      userInfo: state.userInfo,
    }),
  );

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const sourceVideoPrompt = videoPrompts?.[0] || null;

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().remakeFormParams.imagePrompts,
          (prompts) => updateRemakeFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateRemakeFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp videos',
    (refs) =>
      getToolTempVideosPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().remakeFormParams.videoPrompts,
          (prompts) => updateRemakeFormParams('videoPrompts', prompts.filter(Boolean) as ToolTaskFormVideoPrompt[]),
        ),
        { videoPrompts },
      ),
    [updateRemakeFormParams, userInfo, videoPrompts],
  );

  const handleTaskSuccess = useCallback(
    async (task: TaskInfo, inspirationId?: string, source?: string) => {
      const patch = await buildToolTaskRecreatePatch({
        tool: 'remake',
        task,
        baseSettings: settings,
        inspirationId,
        inspirationFrom: source,
      });
      applyToolFormParamsPatch(updateRemakeFormParams, patch);
    },
    [settings, updateRemakeFormParams],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const handleSelectSourceVideo = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedSourceCreationIndex(null);
        setVideoRemakeState({
          selectedSourceCreation: undefined,
        });
        return;
      }
      setSelectedSourceCreationIndex(index);
      setVideoRemakeState({
        selectedSourceCreation: creation,
      });
    },
    [setVideoRemakeState],
  );

  const handleRemoveSourceVideo = useCallback(() => {
    updateRemakeFormParams('videoPrompts', []);
  }, [updateRemakeFormParams]);

  const handleRemoveImage = useCallback(
    (fileId: string) => {
      const newImagePrompts = imagePrompts.filter((imagePrompt) => imagePrompt.id !== fileId);
      updateRemakeFormParams('imagePrompts', newImagePrompts);
    },
    [imagePrompts, updateRemakeFormParams],
  );

  const handleSourceVideoUpload = useMemoizedFn((file: VideoUploadFile) => {
    const videoPrompt = createToolVideoPromptFromUploadFile(file);

    const newVideoPrompts = [...videoPrompts];
    newVideoPrompts[0] = videoPrompt;
    updateRemakeFormParams('videoPrompts', newVideoPrompts);

    if (file.status === 'done' && file.file) {
      updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(file.file?.duration) });
    }

    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveSourceVideo();
    }
  });

  const handleImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    const imagePrompt = createToolImagePromptFromUploadFile(file);

    const currentImagePrompts = storeApi.getState().remakeFormParams.imagePrompts;
    const existingIndex = currentImagePrompts.findIndex((img) => img.id === file.id);

    if (existingIndex !== -1) {
      const newImagePrompts = [...currentImagePrompts];
      newImagePrompts[existingIndex] = imagePrompt;
      updateRemakeFormParams('imagePrompts', newImagePrompts);
    } else if (currentImagePrompts.length < MAX_REFERENCE_IMAGE_COUNT) {
      const newImagePrompts = [...currentImagePrompts, imagePrompt];
      updateRemakeFormParams('imagePrompts', newImagePrompts);
    }

    if (file.status === 'error' || file.status === 'removed') {
      handleRemoveImage(file.id);
    }
  });

  const getSelectedImage = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(blob);
      });
      return { blob, width: img.width, height: img.height };
    } catch {
      return null;
    }
  }, []);

  const imageUploadFiles = imagePrompts.map((imagePrompt) => ({
    id: imagePrompt.id,
    status: imagePrompt.status,
    file: imagePrompt.prompt || null,
  })) as ImageUploadFile[];

  const hasSourceVideo = !!sourceVideoPrompt || !!creationId;
  const hasUploadingMedia =
    imagePrompts.some((prompt) => prompt.status === 'uploading') ||
    videoPrompts.some((prompt) => prompt.status === 'uploading');
  const isSubmitDisabled =
    hasUploadingMedia ||
    !hasSourceVideo ||
    (sourceVideoPrompt && sourceVideoPrompt.status !== 'done') ||
    imagePrompts.length < minImageCount ||
    imagePrompts.some((img) => img.status !== 'done');

  const remainingVideoSlots = sourceVideoPrompt ? 0 : 1;
  const remainingImageSlots = Math.max(0, MAX_REFERENCE_IMAGE_COUNT - imagePrompts.length);

  return (
    <>
      <ToolFormContainer
        className="bg-[#0a0d10] px-5 py-10"
        title={t('tools.videoRemake.title')}
        description={t('tools.videoRemake.description')}
        exampleSection={<ExampleMediaPreview tool="remake" />}
        mediaSection={
          <MediaDropZone
            acceptVideo={true}
            maxVideoCount={remainingVideoSlots}
            maxVideoSize={MAX_VIDEO_FILE_SIZE}
            maxVideoDuration={maxVideoDuration}
            minVideoDuration={minVideoDuration}
            maxImageCount={remainingImageSlots}
            disabled={remainingVideoSlots === 0 && remainingImageSlots === 0}
            getSelectedImage={getSelectedImage}
            allowTempMode={true}
            onChange={(file, type) => {
              if (type === 'video') handleSourceVideoUpload(file as VideoUploadFile);
              else if (type === 'image') handleImageUpload(file as ImageUploadFile);
            }}
          >
            <div className="flex gap-3 md:flex-col">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <ToolMediaUpload
                  type="video"
                  header={
                    <ToolMediaUploadHeader
                      icon={<ProductLineAiVideo className="size-5 text-system-white" />}
                      title={t('tools.videoRemake.sourceVideo.label')}
                    />
                  }
                  surfaceSize="sm"
                  videoPrompt={sourceVideoPrompt}
                  isMobile={isMobile}
                  maxVideoSize={MAX_VIDEO_FILE_SIZE}
                  maxDuration={maxVideoDuration}
                  minDuration={minVideoDuration}
                  onVideoUpload={handleSourceVideoUpload}
                  onOpenSelector={() => setOpenSourceVideoSelector(true)}
                  onRemoveMedia={handleRemoveSourceVideo}
                />
              </div>

              <div className="flex min-w-0 flex-1 flex-col">
                <MultiImageUpload
                  imageList={imageUploadFiles}
                  onImageUpload={handleImageUpload}
                  onRemoveImage={handleRemoveImage}
                  getSelectedImage={getSelectedImage}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="font-medium text-sm text-system-white leading-[1.4]">
                {t('tools.videoRemake.prompt.label')}
              </div>
              <div className="relative flex min-h-[120px] w-full flex-col rounded-16 border border-system-white08 bg-system-white04 p-3 backdrop-blur-[30px]">
                <BasicTextarea
                  placeholder={t('tools.videoRemake.prompt.placeholder')}
                  textPrompt={textPrompt}
                  updateTextPrompt={(value) => updateRemakeFormParams('textPrompt', value)}
                  showClearButton={!!textPrompt}
                  onClearPrompt={() => updateRemakeFormParams('textPrompt', '')}
                />
              </div>
            </div>
          </MediaDropZone>
        }
        settingsSection={<VideoRemakeSettings settings={settings || {}} updateSettings={updateSettings} />}
        submitButton={
          <ToolFormSubmitButton
            taskType="remake"
            disabled={isSubmitDisabled}
            disabledTip={hasUploadingMedia ? t('create.createForm.error.uploadingMedia') : undefined}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />

      <CreationSelectorDialog
        open={openSourceVideoSelector}
        onOpenChange={setOpenSourceVideoSelector}
        selectedIndex={selectedSourceCreationIndex}
        mediaType="video"
        maxDuration={maxVideoDuration}
        minDuration={minVideoDuration}
        onSelectionChange={handleSelectSourceVideo}
        onConfirm={() => {
          setOpenSourceVideoSelector(false);
          const selectedCreation = videoRemakeState.selectedSourceCreation;
          const selectedDuration =
            (isExtendTask(selectedCreation?.task.type)
              ? selectedCreation?.duration
              : selectedCreation?.task.settings.duration) || 0;
          if (
            selectedCreation &&
            isVideoDurationWithinRange({
              duration: selectedDuration,
              minDuration: minVideoDuration,
              maxDuration: maxVideoDuration,
            })
          ) {
            const videoPrompt = createVideoPromptFromCreation(selectedCreation, {
              includeReference: true,
            });
            updateRemakeFormParams('videoPrompts', [videoPrompt]);
            // updateRemakeFormParams('creationId', selectedCreation.id);
            updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(selectedDuration) });
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default VideoRemakeForm;
